import { useToastStore, type ToastType } from '../store/toastStore'

interface ToastOpts {
  title?: string
  duration?: number
  id?: string
}

const push = (type: ToastType, message: string, opts: ToastOpts = {}) =>
  useToastStore.getState().add({ type, message, ...opts })

/**
 * Globální toast API — použitelné odkudkoli (i mimo React, např. axios interceptory).
 *   toast.success('Uloženo')
 *   const id = toast.loading('Nahrávám…'); … toast.success('Hotovo', { id })
 *   await toast.promise(api.post(...), { loading:'Ukládám…', success:'Uloženo', error:'Chyba' })
 */
export const toast = {
  success: (message: string, opts?: ToastOpts) => push('success', message, opts),
  error: (message: string, opts?: ToastOpts) => push('error', message, { duration: 6000, ...opts }),
  warning: (message: string, opts?: ToastOpts) => push('warning', message, { duration: 5000, ...opts }),
  info: (message: string, opts?: ToastOpts) => push('info', message, opts),
  /** Drží, dokud ho neaktualizuješ (přes vrácené id) nebo nezavřeš. */
  loading: (message: string, opts?: ToastOpts) => push('loading', message, { duration: 0, ...opts }),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
  clear: () => useToastStore.getState().clear(),

  /** Toast, který přežije reload stránky (zobrazí se po načtení). Vhodné před window.location redirectem. */
  flash: (message: string, type: ToastType = 'success') => {
    try { sessionStorage.setItem('flash_toast', JSON.stringify({ message, type })) } catch { /* ignore */ }
  },

  /** Obalí promise: loading → success/error. Vrací původní promise. */
  async promise<T>(
    p: Promise<T>,
    msgs: {
      loading: string
      success: string | ((v: T) => string)
      error: string | ((e: any) => string)
    }
  ): Promise<T> {
    const id = push('loading', msgs.loading, { duration: 0 })
    const { update } = useToastStore.getState()
    try {
      const v = await p
      update(id, { type: 'success', message: typeof msgs.success === 'function' ? msgs.success(v) : msgs.success, duration: 4000 })
      return v
    } catch (e) {
      update(id, { type: 'error', message: typeof msgs.error === 'function' ? msgs.error(e) : msgs.error, duration: 6000 })
      throw e
    }
  },
}
